import { Input, Modal, Typography } from 'antd';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmContext } from './context';

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
}

export interface PromptOptions {
  title: ReactNode;
  description?: ReactNode;
  label?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
  /** Verilirse boş girdi bu mesajla engellenir (modal kapanmaz). */
  requiredMessage?: string;
  maxLength?: number;
}

type Resolver = (ok: boolean) => void;
type PromptResolver = (value: string | null) => void;

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['components', 'common']);
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptOpts, setPromptOpts] = useState<PromptOptions | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [promptError, setPromptError] = useState<string | null>(null);
  const promptResolverRef = useRef<PromptResolver | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    setOpts(next);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(ok);
  }, []);

  const prompt = useCallback((next: PromptOptions) => {
    setPromptOpts(next);
    setPromptValue(next.defaultValue ?? '');
    setPromptError(null);
    setPromptOpen(true);
    return new Promise<string | null>((resolve) => {
      promptResolverRef.current = resolve;
    });
  }, []);

  const closePrompt = useCallback(
    (value: string | null) => {
      if (value !== null && promptOpts?.requiredMessage && value.trim().length === 0) {
        setPromptError(promptOpts.requiredMessage);
        return;
      }
      setPromptOpen(false);
      const resolve = promptResolverRef.current;
      promptResolverRef.current = null;
      resolve?.(value === null ? null : value.trim());
    },
    [promptOpts],
  );

  const value = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        title={
          <span className="ds-confirm__title">
            {opts?.danger ? (
              <span className="ds-confirm__icon ds-confirm__icon--danger" aria-hidden>
                <AlertTriangle size={18} />
              </span>
            ) : null}
            {opts?.title}
          </span>
        }
        okText={opts?.okText ?? t('common:actions.confirm')}
        cancelText={opts?.cancelText ?? t('common:actions.cancel')}
        okButtonProps={{ danger: opts?.danger, autoFocus: true }}
        onOk={() => close(true)}
        onCancel={() => close(false)}
        destroyOnHidden
        centered
      >
        {opts?.description ? <div className="ds-confirm__body">{opts.description}</div> : null}
      </Modal>
      <Modal
        open={promptOpen}
        title={
          <span className="ds-confirm__title">
            {promptOpts?.danger ? (
              <span className="ds-confirm__icon ds-confirm__icon--danger" aria-hidden>
                <AlertTriangle size={18} />
              </span>
            ) : null}
            {promptOpts?.title}
          </span>
        }
        okText={promptOpts?.okText ?? t('common:actions.confirm')}
        cancelText={promptOpts?.cancelText ?? t('common:actions.cancel')}
        okButtonProps={{ danger: promptOpts?.danger }}
        onOk={() => closePrompt(promptValue)}
        onCancel={() => closePrompt(null)}
        destroyOnHidden
        centered
      >
        {promptOpts?.description ? (
          <div className="ds-confirm__body">{promptOpts.description}</div>
        ) : null}
        {promptOpts?.label ? (
          <div style={{ fontSize: 13, marginTop: 8, marginBottom: 4 }}>{promptOpts.label}</div>
        ) : null}
        <Input.TextArea
          rows={3}
          value={promptValue}
          maxLength={promptOpts?.maxLength}
          showCount={typeof promptOpts?.maxLength === 'number'}
          placeholder={promptOpts?.placeholder}
          status={promptError ? 'error' : undefined}
          onChange={(e) => {
            setPromptValue(e.target.value);
            if (promptError) setPromptError(null);
          }}
        />
        {promptError ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {promptError}
          </Typography.Text>
        ) : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}
